import React from 'react';
import { Box, Button, Typography } from '@heycater/design-system';
import { BoxProps, Hidden } from '@material-ui/core';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useTheme } from 'styled-components';

import heroImage from '@images/food-program-page/hero.png';
import CheckIcon from '@images/homepage/redesign/check-icon.png';
import { useBreakpointDown } from 'qualification/hooks/useBreakpoints';
import { getRating } from 'shared/services/trustedShops/handleRatings';
import { Ratings } from 'shared/services/trustedShops/model';
import HeroBenefits from 'static_pages/foodProgram/HeroBenefits';
import { useFoodProgramCta } from 'static_pages/foodProgram/useFoodProgramCta';
import { FoodProgramH1 } from 'static_pages/foodProgram/Utils';
import Container from 'system/components/Container';

interface Props extends BoxProps {
  ratings: Ratings;
}

function Hero({ ratings, ...boxProps }: Props) {
  const theme = useTheme();
  const { t } = useTranslation('food_program');
  const ctaHref = useFoodProgramCta();
  const mobile = useBreakpointDown('sm');
  const i18Benefits = t('hero.benefits.items', {
    returnObjects: true,
  }) as string[];

  const benefits = Array.isArray(i18Benefits) ? i18Benefits : [];

  return (
    <React.Fragment>
      <Wrapper as="section" width="100%" {...boxProps}>
        <Content>
          <Container size={1268}>
            <Box
              display="flex"
              justifyContent={{ xs: 'flex-start', md: 'center' }}
              my={{ xs: 4, md: 6 }}
            >
              <Box
                display="block"
                width="100%"
                textAlign={{ xs: 'center', md: 'start' }}
                pr={{ xs: 0, md: 4 }}
              >
                <FoodProgramH1 mb={5}>
                  {t('hero.title')}
                  <Box as="span" color={theme.palette.secondary[400]}>
                    {' '}
                    {t('hero.titleHighlight')}
                  </Box>
                </FoodProgramH1>

                <Benefits>
                  <BenefitsTitle>{t('hero.benefits.title')}</BenefitsTitle>
                  <BenefitsList>
                    {benefits.map((benefit) => (
                      <Benefit key={benefit}>
                        <Box flexShrink={0}>
                          <Image
                            alt=""
                            src={CheckIcon}
                            layout="fixed"
                            width={mobile ? 34 : 38}
                            height={mobile ? 27 : 30}
                          />
                        </Box>
                        <BenefitTypography>{benefit}</BenefitTypography>
                      </Benefit>
                    ))}
                  </BenefitsList>
                </Benefits>
                <Box
                  display="flex"
                  mx={{ xs: 'auto', md: 0 }}
                  flexDirection="column"
                  alignItems="center"
                  width={{ xs: 'fit-content' }}
                >
                  <Typography mb={2} fontWeight={600} fontSize={16}>
                    {t('hero.ctaLabelTop')}
                  </Typography>
                  <Button
                    component="a"
                    target="_blank"
                    href={ctaHref}
                    rounded
                    size="lg"
                    block
                  >
                    {t('cta')}
                  </Button>
                  <Typography mt={2}> {t('hero.ctaLabelBottom')}</Typography>
                </Box>
              </Box>
              <Hidden smDown>
                <Box flexShrink={0}>
                  <Image
                    alt=""
                    priority
                    src={heroImage}
                    width={441}
                    height={443}
                    objectFit="cover"
                  />
                </Box>
              </Hidden>
            </Box>
          </Container>
        </Content>
      </Wrapper>
      <HeroBenefits rating={getRating(ratings)} />
    </React.Fragment>
  );
}

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  z-index: 2;
  ${({ theme }) => theme.breakpoints.up('md')} {
    overflow: visible;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
`;

const BenefitTypography = styled(Typography)`
  color: #3e3e3e;
  font-size: 16px;
  text-align: center;
  font-weight: 400;
  padding: 0px;
  line-height: 168%; /* 33.6px */

  ${({ theme }) => theme.breakpoints.up('sm')} {
    text-align: start;
  }
  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 20px;
  }
`;

const BenefitsList = styled.ul`
  display: flex;
  margin: 0 auto;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  max-width: 70vw;

  list-style: none;
  padding: 0px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin: ${({ theme }) => theme.spacing(0, 0)};
    align-items: flex-start;
  }
`;

const BenefitsTitle = styled.h2`
  width: 100%;
  color: #3e3e3e;
  margin: 0px;
  font-size: 20px;
  font-weight: 600;
  line-height: 172.5%;

  ${({ theme }) => theme.breakpoints.up('lg')} {
    font-size: 24px;
  }
`;

const Benefit = styled.li`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)}px;
`;

const Benefits = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  margin: ${({ theme }) => theme.spacing(0, 0, 3, 0)};

  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin: ${({ theme }) => theme.spacing(0, 0, 6, 0)};
    align-items: flex-start;
  }
`;

export default Hero;
